<template>
  <div class="timeline-view" :class="{ 'timeline-view-embedded': !!embedded }" ref="wrapper">
    <div v-if="!embedded" class="timeline-view-toolbar" :class="{ 'timeline-view-toolbar-narrow': toolbarNarrow }">
      <div class="timeline-view-nav">
        <button type="button" class="btn btn-default btn-xs" :title="_('Previous')" v-on:click="moveBack"><i class="fa fa-arrow-left"></i></button>
        <DatePicker v-model="dateStart" :shortLocale="shortLocale" :staticInput="true" :format="dateFormat.replace(/D/g, 'd').replace(/Y/g, 'y')"></DatePicker>
        <TimePicker v-model="timeStart" :shortLocale="shortLocale" :format="zeroTimeFormat" :disableMinutes="true" v-if="mode === 'hourly'"></TimePicker>
        <button type="button" class="btn btn-default btn-xs" :title="_('Next')" v-on:click="moveForward"><i class="fa fa-arrow-right"></i></button>
        <button type="button" class="btn btn-default btn-xs" :title="_('Today')" v-on:click="moveToToday"><i class="fa fa-calendar-day"></i></button>
      </div>
      <div class="timeline-view-tools">
        <button type="button" class="btn btn-default btn-xs" :title="_('Zoom in')" :disabled="currentZoom === 0" v-on:click="zoomIn"><i class="fa fa-search-plus"></i></button>
        <button type="button" class="btn btn-default btn-xs" :title="_('Zoom out')" :disabled="currentZoom === zoomParams.length - 1" v-on:click="zoomOut"><i class="fa fa-search-minus"></i></button>
      </div>
      <div v-if="legend.length" class="timeline-view-legend">
        <span v-for="item in legend" :key="item.state" class="timeline-view-legend-item"><i class="timeline-view-swatch" :class="'timeline-view-swatch-' + item.state"></i>{{ item.label }}</span>
      </div>
    </div>
    <ScheduleTimeline
      ref="timeline"
      :rows="displayRows"
      :events="displayEvents"
      :openKeys="openSections"
      :unit="unit"
      :visibleColumns="zoomParams[currentZoom].x_size"
      :anchor="anchor"
      :labelWidth="labelWidth"
      :density="density"
      :snapMinutes="15"
      :skew="!!skew && mode === 'daily'"
      :loading="loading"
      :height="embedded ? (fixedHeight || contentHeight) : containerHeight"
      :formatColumn="formatColumn"
      :formatGroup="formatGroup"
      :formatRange="formatRange"
      :canDrop="canDrop"
      :resizeLimit="resizeLimit"
      :canCreate="canCreate"
      :eventClass="eventClass"
      :snapRange="snapRange"
      :fitRange="embedded || null"
      :extraRows="lensRows"
      :ariaLabel="_('Timeline')"
      :collapseLabel="_('Collapse')"
      :expandLabel="_('Expand')"
      v-on:update:anchor="anchorChanged"
      v-on:update:labelWidth="labelWidthChanged"
      v-on:range="rangeChanged"
      v-on:toggle="toggleSection"
      v-on:zoom="zoomGesture"
      v-on:event-click="eventBarClicked"
      v-on:event-change="eventChanged"
      v-on:range-create="rangeCreated"
      v-on:content-height="contentHeightChanged"
    >
      <template #row>
        <DayLens
          v-if="lens"
          :key="lens.key"
          :source="lens.source"
          :title="lensTitle"
          :range="lensRange"
          :shopId="shopId"
          :locale="locale"
          :component="component"
          :column="column"
          :baseDomain="baseDomain"
          :apiKey="apiKey"
          :reloadEvents="reloadEvents"
          :keepTimes="keepTimes"
          :labelWidth="labelWidth"
          :density="density"
          v-on:close="closeLens"
          v-on:navigate="navigateLens"
          v-on:height="lensHeightChanged"
        />
      </template>
      <template #corner="{ collapsed }">
        <button v-if="!embedded && !collapsed" type="button" class="timeline-view-density" :title="densityTitles[nextDensity]" v-on:click="toggleDensity">
          <i class="fa" :class="nextDensity === 'compact' ? 'fa-compress-alt' : 'fa-expand-alt'"></i>
        </button>
      </template>
      <template #label="{ row }">
        <span>{{ row.label }}</span>
        <i v-if="row.meta && row.meta.shared !== undefined" class="fa timeline-view-shared" :class="parseInt(row.meta.shared) ? 'fa-users' : 'fa-users-slash'"></i>
      </template>
      <template #label-end="{ row }">
        <i v-if="isProductKey(row.key) && row.meta" class="fa fa-info-circle timeline-view-info" v-on:click.stop="showPreviewModal(row.meta.shopid, row.meta.source_id)"></i>
      </template>
      <template #event="{ event, placement, cellWidth, windowStart }">
        <span v-if="event.data.kind === 'band'" class="timeline-view-band" v-on:pointermove="bandHover($event, event)" v-on:pointerleave="hover = null">
          <template v-for="ticks in [bandTicks(event, placement, cellWidth, windowStart)]" :key="'ticks'">
            <span v-if="ticks && !Array.isArray(ticks)" class="timeline-view-ticks" :style="{ left: ticks.left + 'px', width: ticks.width + 'px', backgroundSize: ticks.spacing + 'px 100%' }"></span>
            <span v-for="(mark, i) in (Array.isArray(ticks) ? ticks : [])" :key="i" class="timeline-view-tick" :style="{ left: mark + 'px' }"></span>
          </template>
          <span v-if="parseInt(event.data.capacity) && placement.width >= 24" class="timeline-view-center timeline-view-capacity">{{ event.data.capacity }}</span>
        </span>
        <template v-else-if="event.data.kind === 'daysummary'">
          <span class="timeline-view-center timeline-view-summary">{{ summaryLabel(event, placement) }}</span>
          <span v-if="event.data.buckets && placement.width >= 18" class="timeline-view-strip" :style="{ background: stripGradient(event) }"></span>
        </template>
        <span v-else-if="event.data.kind === 'occupancy'" class="timeline-view-center timeline-view-occupancy">
          <template v-for="label in [occupancyLabel(event, placement)]" :key="'label'">{{ label.count }}<template v-if="label.participants"><span class="timeline-view-occupancy-sep">/</span><i class="fa fa-user"></i>{{ label.participants }}</template><span v-if="label.duration" class="timeline-view-occupancy-duration">{{ label.duration }}</span></template>
        </span>
        <span v-else-if="isProductKey(event.rowKeys[0])" class="timeline-view-center timeline-view-capacity">{{ parseInt(event.data.capacity) ? event.data.capacity : '' }}</span>
        <span v-else-if="isPoolKey(event.rowKeys[0]) || isOverviewKey(event.rowKeys[0])" class="timeline-view-center timeline-view-occupancy" :title="overviewTitle(event)">
          <template v-if="event.data.kind === 'resoverview'"><template v-if="placement.width >= 60">{{ event.data.count }}<span class="timeline-view-occupancy-sep">/</span></template><i v-if="placement.width >= 36" class="fa fa-user"></i>{{ event.data.participants }}</template>
          <template v-else>{{ event.data.text }}</template>
        </span>
        <span v-else class="timeline-view-res" :class="{ 'timeline-view-res-two': density === 'relaxed' }">
          <template v-for="parts in [reservationParts(event, placement.width)]" :key="'parts'">
            <span class="timeline-view-res-line">
              <span v-if="parts.count || parts.person" class="timeline-view-res-count" :title="parts.status ? checkinText(parts.status) : null">
                <template v-if="parts.person"><i v-if="parts.status === 'in'" class="timeline-view-res-dot"></i><i v-else class="fa" :class="parts.status === 'out' ? 'fa-right-from-bracket' : 'fa-user'"></i></template>{{ parts.count || '' }}
              </span>
              <span v-if="parts.name" class="timeline-view-res-name">{{ event.data.text }}</span>
              <i v-if="parts.notes" class="fa fa-sticky-note timeline-view-res-notes"></i>
              <span v-if="parts.channel" class="timeline-view-res-channel" :title="channelNames[parts.channel]">
                <i v-if="parts.channel === 'other'" class="fa fa-globe"></i>
                <span v-else-if="parts.channel === 'gyg'" class="timeline-view-res-channel-text">GYG</span>
                <img v-else :src="'/img/channels/' + parts.channel + '.png'" alt="" width="14" height="14">
              </span>
            </span>
            <span v-if="parts.detail && (parts.detail.time || parts.detail.text)" class="timeline-view-res-line timeline-view-res-detail" :title="parts.status ? checkinText(parts.status) : null">
              <span v-if="parts.detail.time" class="timeline-view-res-time">{{ parts.detail.time }}</span>
              <span v-if="parts.detail.text" class="timeline-view-res-status">{{ parts.detail.text }}</span>
            </span>
          </template>
        </span>
      </template>
    </ScheduleTimeline>
    <DayLens
      v-if="lens && lens.sheet"
      sheet
      :productId="lens.productId"
      :title="lensTitle"
      :range="lensRange"
      :shopId="shopId"
      :locale="locale"
      :component="component"
      :column="column"
      :baseDomain="baseDomain"
      :apiKey="apiKey"
      :reloadEvents="reloadEvents"
      :keepTimes="keepTimes"
      :labelWidth="labelWidth"
      :density="density"
      v-on:close="closeLens"
      v-on:navigate="navigateLens"
    />
    <!-- The lens row clips overflow, so its tooltip is drawn on the page instead -->
    <Teleport to="body" :disabled="!embedded">
      <div v-if="hoverInfo" ref="tooltip" class="timeline-view-tooltip" :class="{ 'timeline-view-tooltip-fixed': embedded }" :style="{ left: hoverInfo.x + 'px', top: hoverInfo.y + 'px' }">
        <div class="timeline-view-tooltip-time">{{ hoverInfo.time }}</div>
        <div v-for="line in hoverInfo.lines" :key="line">{{ line }}</div>
      </div>
    </Teleport>
    <!-- The day lens sits in a sticky row whose stacking context would trap the booking dialog -->
    <Teleport to="body" :disabled="!embedded">
      <component v-if="product" :column="column" :activeComponent="component" :mode="mode" :baseDomain="baseDomain" :key="product.id" :is="'ReservableProductListItem'" :product="product" :productShopId="productShopId" :auto="true" :summary="true" :summaryStart="summaryStart" :summaryEnd="summaryEnd" :allowResourceSelection="true" :preselectedResource="preselectedResource" :defaultStartDate="productStartDate" :defaultEndDate="productEndDate" v-on:productModalClosed="productModalClosed" v-on:success="reservationAddedToBasket">
        {{ product.name }}
      </component>
    </Teleport>
  </div>
</template>

<script>
import axios from 'axios'
import { markRaw } from 'vue'
import { DateTime } from 'luxon'
import debounce from 'lodash/debounce'
import { mapGetters } from 'vuex'
import errors from '~/lib/errors'
import events from '~/lib/events'
import modals from '~/lib/modals'
import { createRangeLoader, formatLoadDate, parseScheduleDate } from '~/lib/schedule/adapter'
import { aggregateOccupancy, atMinute, bucketGradient, dateKey, endTimesFor, formatDuration, isBand, isBookable, isDaySummary, minuteOfDay, nearestEnd, nearestStart, startForTap, tickPattern } from '~/lib/schedule/availability'
import { collectInitiallyOpen, findRootRow } from '~/lib/schedule/rowTree'
import { addUnits, startOfUnit } from '~/lib/schedule/timeScale'
import { DirectReservation, ReservableProductListItem, ReservationPreview, ProductPopup, ScheduleTimeline } from '#components'

// The first levels show a day or an hour with the neighbours peeking in at the edges
const DAILY_ZOOM = [
  { x_size: 1.5, x_length: 1 },
  { x_size: 2, x_length: 1 },
  { x_size: 4, x_length: 1 },
  { x_size: 7, x_length: 1 },
  { x_size: 15, x_length: 3 },
  { x_size: 30, x_length: 7 },
  { x_size: 45, x_length: 7 },
  { x_size: 60, x_length: 7 }
]
const CHANNEL_NAMES = {
  booking: 'Booking.com',
  bokun: 'Bókun',
  gyg: 'GetYourGuide',
  trip: 'Trip.com',
  airbnb: 'Airbnb',
  expedia: 'Expedia',
  agoda: 'Agoda',
  hotels: 'Hotels.com',
  nettimokki: 'Nettimökki',
  johku: 'Johku',
  app: 'Johku App',
  other: ''
}
// Estimated widths in pixels, gap included, used to decide what fits in a reservation block
const RES_PADDING = 14
const RES_DIGIT = 7
const RES_PERSON = 14
const RES_CHANNEL = 18
const RES_NOTES = 15
const RES_NAME_MIN = 28
const RES_CHAR = 6

const DENSITIES = ['compact', 'relaxed']
const OVERVIEW_PREFIX = 'overview:'

// Time spans where the same reservations overlap. The count and the participant total belong to that span, not to its busiest instant.
function overviewSegments (events) {
  const points = []
  for (const event of events) {
    const participants = parseInt(event.data && event.data.participants) || 0
    points.push({ t: event.start.getTime(), participants, count: 1 })
    points.push({ t: event.end.getTime(), participants: -participants, count: -1 })
  }
  points.sort((a, b) => a.t - b.t)
  const segments = []
  let participants = 0
  let count = 0
  for (let i = 0; i < points.length; i++) {
    participants += points[i].participants
    count += points[i].count
    const next = points[i + 1]
    if (!next || next.t === points[i].t || count <= 0) {
      continue
    }
    const last = segments[segments.length - 1]
    if (last && last.end.getTime() === points[i].t && last.participants === participants && last.count === count) {
      last.end = new Date(next.t)
    } else {
      segments.push({ start: new Date(points[i].t), end: new Date(next.t), participants, count })
    }
  }
  return segments
}

// One block per day: how many reservations touch that day, and their participants added up
function dailyTotals (events) {
  const days = new Map()
  for (const event of events) {
    const participants = parseInt(event.data && event.data.participants) || 0
    const from = startOfUnit(event.start, 'day').getTime()
    const last = startOfUnit(new Date(Math.max(event.start.getTime(), event.end.getTime() - 1)), 'day').getTime()
    for (let day = from; day <= last; day = addUnits(new Date(day), 'day', 1).getTime()) {
      let bucket = days.get(day)
      if (!bucket) {
        bucket = { start: new Date(day), end: addUnits(new Date(day), 'day', 1), count: 0, participants: 0 }
        days.set(day, bucket)
      }
      bucket.count++
      bucket.participants += participants
    }
  }
  return [...days.values()].sort((a, b) => a.start - b.start)
}

function reservedLevel (participants, seats) {
  if (!seats) {
    return 0
  }
  let level = Math.round((participants / seats) * 10) * 10
  if (level === 0 && participants > 0) {
    level = 10
  }
  if (level >= 100 && participants < seats) {
    level = 90
  }
  return Math.min(100, Math.max(0, level))
}
const LEGACY_ZOOM_OFFSET = 2

// Narrower views put the legend on its own row below the navigation and zoom buttons
const TOOLBAR_WIDE = 900

const HOURLY_ZOOM = [
  { x_size: 1.5, x_length: 1 },
  { x_size: 3, x_length: 1 },
  { x_size: 6, x_length: 3 },
  { x_size: 12, x_length: 6 },
  { x_size: 24, x_length: 12 }
]

function toLuxonDateFormat (format) {
  return (format || 'D.M.YYYY').replace(/YYYY/g, 'yyyy').replace(/DD/g, 'dd').replace(/D/g, 'd')
}

function toLuxonTimeFormat (format) {
  return (format || 'H.mm').replace(/A/g, 'a')
}

export default {
  name: 'TimelineView',
  mixins: [ errors, events, modals ],
  // embedded is a fixed { start, end } range for the day lens, which fits it to the width and keeps no view state of its own
  props: ['url', 'products', 'resourcePools', 'resources', 'shopId', 'locale', 'component', 'column', 'baseDomain', 'apiKey', 'reloadEvents', 'weekStart', 'productFilter', 'resourceFilter', 'mode', 'skew', 'keepTimes', 'maxHeight', 'embedded', 'fixedHeight', 'embeddedLabelWidth', 'embeddedDensity'],
  emits: ['content-height'],
  components: {
    ReservableProductListItem,
    ScheduleTimeline
  },
  data () {
    return {
      rows: [],
      timelineEvents: [],
      loading: false,
      openSections: [],
      openSectionsInitialized: false,
      anchor: startOfUnit(new Date(), 'day'),
      currentZoom: 5,
      labelWidth: 400,
      density: 'relaxed',
      containerHeight: null,
      product: null,
      productShopId: null,
      productStartDate: null,
      productEndDate: null,
      preselectedResource: null,
      summaryStart: null,
      summaryEnd: null,
      observer: null,
      contentHeight: null,
      lens: null,
      lensHeight: 260,
      hover: null,
      tooltipSize: { w: 220, h: 88 },
      toolbarNarrow: true,
      timesVersion: 0
    }
  },
  computed: {
    ...mapGetters(['dateFormat', 'timeFormat', 'pickerDateFormat', 'pickerTimeFormat']),
    unit () {
      return this.mode === 'hourly' ? 'hour' : 'day'
    },
    zoomParams () {
      return this.mode === 'hourly' ? HOURLY_ZOOM : DAILY_ZOOM
    },
    channelNames () {
      return CHANNEL_NAMES
    },
    nextDensity () {
      return DENSITIES[(DENSITIES.indexOf(this.density) + 1) % DENSITIES.length]
    },
    densityTitles () {
      return { compact: this._('Compact view'), relaxed: this._('Relaxed view') }
    },
    zeroTimeFormat () {
      return this.timeFormat.replace(/mm/, '00')
    },
    shortLocale () {
      return (this.locale || 'en_US').split('_')[0]
    },
    luxonLocale () {
      return this.shortLocale === 'nb' ? 'nb' : this.shortLocale
    },
    storageKey () {
      return 'pos-' + this.shopId + '-component-' + this.component.id
    },
    checkinDisplay () {
      let data = null
      try {
        data = this.component && this.component.data ? JSON.parse(this.component.data) : null
      } catch (e) {}
      return data && (data.checkinDisplay === 'show' || data.checkinDisplay === 'hide') ? data.checkinDisplay : 'default'
    },
    viewIdentity () {
      return { storageKey: this.storageKey, mode: this.mode }
    },
    // The visible span plus one screen on each side. The scroll buffer of the timeline is much wider and is not loaded up front.
    loadRange () {
      if (this.embedded) {
        return null
      }
      const span = Math.ceil(this.zoomParams[Math.min(this.currentZoom, this.zoomParams.length - 1)].x_size)
      const start = addUnits(startOfUnit(this.anchor, this.unit), this.unit, -span)
      return { start, end: addUnits(start, this.unit, span * 3) }
    },
    loadRangeKey () {
      return this.loadRange ? this.loadRange.start.getTime() + '-' + this.loadRange.end.getTime() : ''
    },
    isResourceSchedule () {
      return !!((this.resources && this.resources.length) || (this.resourcePools && this.resourcePools.length))
    },
    dateStart: {
      get () {
        return startOfUnit(this.anchor, 'day')
      },
      set (value) {
        if (!value) {
          return
        }
        const day = startOfUnit(value, 'day')
        if (day.getTime() === startOfUnit(this.anchor, 'day').getTime()) {
          return
        }
        const next = new Date(day.getTime())
        if (this.mode === 'hourly') {
          next.setHours(this.anchor.getHours())
        }
        this.anchor = next
        this.saveViewState()
      }
    },
    timeStart: {
      get () {
        return { hours: this.anchor.getHours(), minutes: 0, seconds: 0 }
      },
      set (value) {
        if (!value || value.hours === undefined || value.hours === null) {
          return
        }
        const next = startOfUnit(this.anchor, 'day')
        next.setHours(value.hours)
        this.anchor = next
        this.saveViewState()
      }
    },
    scheduleLoadUrl () {
      let url = this.url
      if (!this.openSections || !this.openSections.length) {
        return url
      }
      // Resource schedule accepts numeric pool ids; product schedule accepts full keys
      const openParams = []
      const seen = Object.create(null)
      for (let i = 0; i < this.openSections.length; i++) {
        const key = String(this.openSections[i])
        const poolOnly = key.match(/^pool-(\d+)$/)
        if (poolOnly && !seen[poolOnly[1]]) {
          openParams.push(poolOnly[1])
          seen[poolOnly[1]] = true
        }
        if ((key.match(/^product-\d+$/) || key.match(/^pool-\d+/)) && !seen[key]) {
          openParams.push(key)
          seen[key] = true
        }
      }
      if (!openParams.length) {
        return url
      }
      return url + (url.indexOf('?') === -1 ? '?' : '&') + 'open=' + openParams.join(',')
    },
    singleSeatKeys () {
      const keys = new Set()
      const walk = (nodes) => {
        for (const node of nodes) {
          if (node.meta && parseInt(node.meta.seats) === 1 && !parseInt(node.meta.shared)) {
            keys.add(node.key)
          }
          if (node.children) {
            walk(node.children)
          }
        }
      }
      walk(this.rows || [])
      return keys
    },
    rootKeys () {
      const map = new Map()
      const walk = (nodes, rootKey) => {
        for (const node of nodes) {
          map.set(node.key, rootKey || node.key)
          if (node.children) {
            walk(node.children, rootKey || node.key)
          }
        }
      }
      walk(this.rows || [], null)
      return map
    },
    // Products without daily lengths are shown as day summaries on the daily scale
    hourlyRoots () {
      return new Set((this.rows || []).filter(row => row.meta && row.meta.pricing === 'hourly').map(row => row.key))
    },
    displayEvents () {
      const overview = this.overviewEvents.concat(this.poolSummaryEvents)
      return overview.length ? markRaw(this.baseEvents.concat(overview)) : this.baseEvents
    },
    baseEvents () {
      // The resource timeline draws its own pool summary, so the sliced seat-count blocks from the server are dropped
      const events = this.isResourceSchedule
        ? this.timelineEvents.filter(event => !/^overview-/.test(String(event.id)))
        : this.timelineEvents
      if (this.mode !== 'daily' || this.embedded) {
        return events === this.timelineEvents ? events : markRaw(events)
      }
      if (this.isResourceSchedule) {
        const overlapping = this.overlapDays
        if (!overlapping.size) {
          return events
        }
        return markRaw(aggregateOccupancy(events, (key, day) => overlapping.has(key + '|' + day)))
      }
      if (!this.hourlyRoots.size) {
        return events
      }
      // Short reservations of hourly products would be slivers on the daily scale, so they are shown per day
      const roots = this.rootKeys
      const hourly = this.hourlyRoots
      return markRaw(aggregateOccupancy(events, key => !this.isProductKey(key) && hourly.has(roots.get(key))))
    },
    // On the hourly scale, resources with several seats get an overview row above their reservation lanes, which can be folded away.
    // The daily scale shows those reservations on the resource's own row.
    overviewKeys () {
      if (!this.isResourceSchedule || this.embedded || this.mode !== 'hourly') {
        return new Set()
      }
      const keys = new Set()
      const walk = nodes => {
        for (const node of nodes || []) {
          if (this.isResourceKey(node.key) && node.meta && parseInt(node.meta.seats) > 1) {
            keys.add(node.key)
          }
          walk(node.children)
        }
      }
      walk(this.rows)
      return keys
    },
    displayRows () {
      const keys = this.overviewKeys
      if (!keys.size) {
        return this.rows
      }
      const map = nodes => nodes.map(node => {
        if (keys.has(node.key)) {
          const { shared, ...meta } = node.meta || {}
          return {
            key: OVERVIEW_PREFIX + node.key,
            label: node.label,
            level: node.level,
            open: false,
            meta: node.meta,
            children: [Object.assign({}, node, { label: this._('Reservations'), level: node.level + 1, meta })]
          }
        }
        return node.children ? Object.assign({}, node, { children: map(node.children) }) : node
      })
      return markRaw(map(this.rows || []))
    },
    overviewEvents () {
      const keys = this.overviewKeys
      if (!keys.size) {
        return []
      }
      const seatsOf = new Map()
      const walk = nodes => {
        for (const node of nodes || []) {
          if (keys.has(node.key)) {
            seatsOf.set(node.key, parseInt(node.meta.seats))
          }
          walk(node.children)
        }
      }
      walk(this.rows)
      const byKey = new Map()
      for (const event of this.timelineEvents) {
        if (event.band || !/^\d+$/.test(event.id)) {
          continue
        }
        for (const key of event.rowKeys) {
          if (keys.has(key)) {
            if (!byKey.has(key)) {
              byKey.set(key, [])
            }
            byKey.get(key).push(event)
          }
        }
      }
      const result = []
      for (const [key, events] of byKey) {
        const seats = seatsOf.get(key)
        const spans = overviewSegments(events)
        for (const span of spans) {
          result.push({
            id: 'resoverview-' + key + '-' + span.start.getTime(),
            start: span.start,
            end: span.end,
            rowKeys: [OVERVIEW_PREFIX + key],
            draggable: false,
            resizable: false,
            className: 'reserved_' + reservedLevel(span.participants, seats),
            data: { kind: 'resoverview', count: span.count, participants: span.participants, seats }
          })
        }
      }
      return result
    },
    // One summary block per pool. Daily is one block per day; hourly keeps each stretch of the same reservations. Neither stacks into extra lanes.
    poolSummaryEvents () {
      if (!this.isResourceSchedule || this.embedded) {
        return []
      }
      const pools = []
      const walk = nodes => {
        for (const node of nodes || []) {
          if (this.isPoolKey(node.key)) {
            const resources = new Set()
            let seats = 0
            const collect = children => {
              for (const child of children || []) {
                if (this.isResourceKey(child.key)) {
                  resources.add(child.key)
                  seats += parseInt(child.meta && child.meta.seats) || 0
                }
                collect(child.children)
              }
            }
            collect(node.children)
            if (resources.size) {
              pools.push({ key: node.key, resources, seats })
            }
          }
          walk(node.children)
        }
      }
      walk(this.rows)
      if (!pools.length) {
        return []
      }
      const reservations = []
      for (const event of this.timelineEvents) {
        if (!event.band && (/^\d+$/.test(String(event.id)) || String(event.id).indexOf('group-') === 0)) {
          reservations.push(event)
        }
      }
      const result = []
      for (const pool of pools) {
        const seen = new Set()
        const events = []
        for (const event of reservations) {
          if (seen.has(event.id) || !event.rowKeys.some(key => pool.resources.has(key))) {
            continue
          }
          seen.add(event.id)
          events.push(event)
        }
        const spans = this.mode === 'daily' ? dailyTotals(events) : overviewSegments(events)
        for (const span of spans) {
          result.push({
            id: 'poolsummary-' + pool.key + '-' + span.start.getTime(),
            start: span.start,
            end: span.end,
            rowKeys: [pool.key],
            band: true,
            draggable: false,
            resizable: false,
            className: 'reserved_' + reservedLevel(span.participants, pool.seats),
            data: { kind: 'resoverview', count: span.count, participants: span.participants, seats: pool.seats }
          })
        }
      }
      return result
    },
    // Resource rows and days where same-day reservations overlap and would stack into several lanes
    overlapDays () {
      const byDay = new Map()
      for (const event of this.timelineEvents) {
        if (event.band || !/^\d+$/.test(event.id)) {
          continue
        }
        const day = startOfUnit(event.start, 'day').getTime()
        if (startOfUnit(new Date(event.end.getTime() - 1), 'day').getTime() !== day) {
          continue
        }
        for (const key of event.rowKeys) {
          if (!this.isResourceKey(key)) {
            continue
          }
          const id = key + '|' + day
          if (!byDay.has(id)) {
            byDay.set(id, [])
          }
          byDay.get(id).push(event)
        }
      }
      const result = new Set()
      for (const [id, events] of byDay) {
        if (events.length < 2) {
          continue
        }
        events.sort((a, b) => a.start - b.start)
        let lastEnd = events[0].end.getTime()
        for (let i = 1; i < events.length; i++) {
          if (events[i].start.getTime() < lastEnd) {
            result.add(id)
            break
          }
          lastEnd = Math.max(lastEnd, events[i].end.getTime())
        }
      }
      return result
    },
    lensRows () {
      if (!this.lens || this.lens.sheet) {
        return []
      }
      return [{ afterKey: this.lens.key, row: { key: 'lens:' + this.lens.key, label: '', level: 0 }, height: this.lensHeight }]
    },
    lensRange () {
      if (!this.lens) {
        return null
      }
      return { start: new Date(atMinute(this.lens.day, this.lens.from)), end: new Date(atMinute(this.lens.day, this.lens.to)) }
    },
    lensTitle () {
      if (!this.lens) {
        return ''
      }
      return this.lens.label + ' · ' + DateTime.fromMillis(this.lens.day).setLocale(this.luxonLocale).toFormat('ccc ' + toLuxonDateFormat(this.dateFormat))
    },
    legend () {
      if (!this.products || this.embedded) {
        return []
      }
      if (this.mode === 'hourly') {
        return [
          { state: 'free', label: this._('Available') },
          { state: 'low', label: this._('Low availability') },
          { state: 'none', label: this._('Not reservable') },
          { state: 'closed', label: this._('Closed') }
        ]
      }
      if (!this.hourlyRoots.size) {
        return []
      }
      return [
        { state: 'free', label: this._('Reservable') },
        { state: 'partial', label: this._('Partially booked') },
        { state: 'none', label: this._('Not reservable') }
      ]
    },
    hoverInfo () {
      const hover = this.hover
      if (!hover) {
        return null
      }
      // Recomputed when lazily loaded end times arrive
      this.timesVersion // eslint-disable-line no-unused-expressions
      const data = hover.event.data
      const lines = []
      const timeFormat = toLuxonTimeFormat(this.pickerTimeFormat)
      if (isBookable(hover.event)) {
        lines.push(this._('Capacity') + ': ' + data.capacity)
        const start = startForTap(hover.event, hover.time, s => endTimesFor(this.cachedTimes(data, s), s))
        if (start !== null) {
          lines.push(this._('Start') + ': ' + DateTime.fromMillis(start).toFormat(timeFormat))
          const times = this.cachedTimes(data, start)
          const ends = endTimesFor(times, start)
          if (ends.length) {
            const lengths = ends.slice(0, 6).map(end => formatDuration((end - start) / 60000, this._('h'), this._('min')))
            lines.push(this._('Lengths') + ': ' + lengths.join(', ') + (ends.length > 6 ? ', …' : ''))
          }
        }
      } else {
        lines.push(data.state === 'closed' ? this._('Closed') : this._('Not reservable'))
      }
      return { x: hover.x, y: hover.y, time: DateTime.fromMillis(hover.time).toFormat(timeFormat), lines }
    }
  },
  watch: {
    url () {
      this.createLoader()
    },
    loadRangeKey () {
      if (this.loader && this.loadRange) {
        this.loader.ensure(this.loadRange.start, this.loadRange.end)
      }
    },
    embedded (value) {
      if (value) {
        this.anchor = startOfUnit(value.start, 'hour')
      }
    },
    embeddedLabelWidth (value) {
      if (this.embedded && value) {
        this.labelWidth = value
      }
    },
    embeddedDensity (value) {
      if (this.embedded && value) {
        this.density = value
      }
    },
    viewIdentity (value, oldValue) {
      if (this.embedded) {
        return
      }
      // Layout tabs reuse this instance for another component; swap view state before the next render
      if (value.storageKey !== oldValue.storageKey) {
        this.saveViewStateToKey(oldValue.storageKey)
        this.restoreViewState()
        return
      }
      this.currentZoom = Math.min(this.currentZoom, this.zoomParams.length - 1)
      const anchor = startOfUnit(this.anchor, 'day')
      if (value.mode === 'hourly') {
        anchor.setHours(8)
      }
      this.anchor = anchor
    }
  },
  methods: {
    isProductKey (key) {
      return !!(key && /^product/.test(key))
    },
    isPoolKey (key) {
      return !!(key && /^pool/.test(key))
    },
    isResourceKey (key) {
      return !!key && !this.isProductKey(key) && !this.isPoolKey(key) && !this.isOverviewKey(key)
    },
    isOverviewKey (key) {
      return !!key && key.indexOf(OVERVIEW_PREFIX) === 0
    },

    // Formatting

    formatColumn (date, cellWidth) {
      const dt = DateTime.fromJSDate(date).setLocale(this.luxonLocale)
      if (this.mode === 'hourly') {
        if (cellWidth < 44) {
          return dt.toFormat(/a/.test(toLuxonTimeFormat(this.pickerTimeFormat)) ? 'h a' : 'H')
        }
        return dt.toFormat(toLuxonTimeFormat(this.pickerTimeFormat))
      }
      return cellWidth >= 40 ? dt.toFormat('ccc d') : dt.toFormat('d')
    },
    formatGroup (date) {
      const dt = DateTime.fromJSDate(date).setLocale(this.luxonLocale)
      if (this.mode === 'hourly') {
        return dt.toFormat('cccc ' + toLuxonDateFormat(this.dateFormat))
      }
      return dt.toFormat('LLLL yyyy')
    },
    formatRange (start, end) {
      const s = DateTime.fromJSDate(start)
      const e = DateTime.fromJSDate(end)
      if (this.mode === 'hourly') {
        const timeFormat = toLuxonTimeFormat(this.pickerTimeFormat)
        return s.toFormat(timeFormat) + ' – ' + e.toFormat(timeFormat) + ' · ' + formatDuration((end.getTime() - start.getTime()) / 60000, this._('h'), this._('min'))
      }
      const dateFormat = toLuxonDateFormat(this.dateFormat)
      return s.toFormat(dateFormat) + ' – ' + e.toFormat(dateFormat)
    },
    eventClass (event) {
      const data = event.data || {}
      const now = Date.now()
      let timing = ''
      if (event.start.getTime() < now && event.end.getTime() > now) {
        timing = 'sv-current'
      } else if (event.end.getTime() < now) {
        timing = 'sv-past'
      } else {
        timing = 'sv-upcoming'
      }
      const rowKey = event.rowKeys[0]
      if (this.isProductKey(rowKey)) {
        if (data.kind === 'band') {
          return 'sv-band sv-band-' + data.state + ' ' + timing
        }
        if (data.kind === 'daysummary') {
          return 'sv-daysummary sv-daysummary-' + data.state + ' ' + timing
        }
        return (parseInt(data.capacity) > 0 ? 'sv-available' : 'sv-unavailable') + ' st-slant ' + timing
      }
      if (data.kind === 'occupancy') {
        const lensOpen = this.lens && (rowKey === this.lens.key || this.rootKeys.get(rowKey) === this.lens.key)
        return 'sv-occupancy ' + timing + (lensOpen ? ' sv-dimmed' : '')
      }
      if (this.isPoolKey(rowKey) || this.isOverviewKey(rowKey)) {
        return 'sv-overview ' + timing
      }
      switch (data.order_type) {
        case 'none':
          return 'sv-reservation ' + timing
        case 'open':
          return 'sv-open ' + timing
        case 'confirmed':
          return 'sv-confirmed ' + timing
        case 'notpaid':
          return 'sv-unpaid ' + timing
        case 'paid':
          return 'sv-paid ' + timing
        case 'partial':
          return 'sv-partial ' + timing
      }
      return 'sv-default ' + timing
    },

    // Rules for dragging and creating

    decorateEvent (event) {
      const rowKey = event.rowKeys[0]
      const isReservation = /^\d+$/.test(event.id) && this.isResourceKey(rowKey)
      event.draggable = isReservation
      event.resizable = isReservation
      // The daily payload rounds multi-day reservations to midnight for the legacy timeline
      if (isReservation) {
        const realStart = parseScheduleDate(event.data.real_start_date)
        const realEnd = parseScheduleDate(event.data.real_end_date)
        if (realStart && realEnd && realEnd > realStart) {
          event.start = realStart
          event.end = realEnd
        }
      }
      if (isBand(event)) {
        event.band = true
        event.selectable = isBookable(event)
      }
      return event
    },
    canDrop (event, row) {
      return this.isResourceKey(row.key)
    },
    // A resource with one seat that can't be shared can't overlap, so resizing stops at the next booking or block
    resizeLimit (event, edge) {
      const keys = event.rowKeys.filter(key => this.singleSeatKeys.has(key))
      if (!keys.length) {
        return null
      }
      let limit = null
      for (const other of this.timelineEvents) {
        if (other.id === event.id || other.band || other.data.kind === 'occupancy' || !other.rowKeys.some(key => keys.includes(key))) {
          continue
        }
        if (edge === 'end' && other.start >= event.end && (!limit || other.start < limit)) {
          limit = other.start
        } else if (edge === 'start' && other.end <= event.start && (!limit || other.end > limit)) {
          limit = other.end
        }
      }
      return limit
    },
    canCreate (row) {
      if (this.isResourceSchedule) {
        return this.isResourceKey(row.key)
      }
      return !!this.rootRowOf(row.key)
    },
    rootRowOf (key) {
      // The day lens shows the product as a plain first row followed by its former children
      if (this.embedded) {
        return this.isProductKey(key) ? null : this.rows[0] || null
      }
      return findRootRow(this.rows, key)
    },

    // Availability bands

    bandsOfRow (rowKey) {
      return this.timelineEvents.filter(ev => isBand(ev) && ev.rowKeys[0] === rowKey)
    },
    bandTicks (event, placement, cellWidth, windowStart) {
      if (!event.data.starts) {
        return null
      }
      const pxPerMinute = cellWidth / 60
      const offset = placement.clippedStart ? ((windowStart - event.start.getTime()) / 60000) * pxPerMinute : 0
      return tickPattern(event, pxPerMinute, offset)
    },
    // Parts are dropped as the block narrows: the name truncates, then the name, notes, person icon and channel go, and the count stays
    reservationParts (event, width) {
      const data = event.data || {}
      const participants = parseInt(data.participants) || 0
      const channel = CHANNEL_NAMES[data.channel] !== undefined ? data.channel : null
      let room = width - RES_PADDING - String(participants || '').length * RES_DIGIT
      let fits = true
      const take = (wanted, cost) => {
        if (!wanted) {
          return false
        }
        if (!fits || room < cost) {
          fits = false
          return false
        }
        room -= cost
        return true
      }
      const status = this.checkinStatus(data)
      const parts = { count: participants || 0, status, channel: null, person: false, notes: false, name: false, detail: null }
      parts.channel = take(!!channel, RES_CHANNEL) ? channel : null
      parts.person = take(participants > 0 || (!!status && status !== 'waiting'), RES_PERSON)
      parts.notes = take(!!parseInt(data.hasnotes), RES_NOTES)
      parts.name = !!data.text && ((fits && room >= RES_NAME_MIN) || !participants)
      if (this.density === 'relaxed') {
        // The second line adds the time and then the status text when they fit
        const time = this.reservationTime(event)
        const text = status ? this.checkinText(status) : ''
        let detailRoom = width - RES_PADDING
        parts.detail = { time: '', text: '' }
        if (detailRoom >= time.length * RES_CHAR) {
          parts.detail.time = time
          detailRoom -= time.length * RES_CHAR + 4
          if (detailRoom >= text.length * RES_CHAR) {
            parts.detail.text = text
          }
        }
      }
      return parts
    },
    reservationTime (event) {
      const format = toLuxonTimeFormat(this.pickerTimeFormat)
      return DateTime.fromJSDate(event.start).toFormat(format) + '–' + DateTime.fromJSDate(event.end).toFormat(format)
    },
    // Null hides the status. Bookings without customer details rarely check in, so by default they only show a status once it has changed
    checkinStatus (data) {
      if (parseInt(data.maintenance)) {
        return null
      }
      const status = data.checkin === 'in' || data.checkin === 'out' ? data.checkin : 'waiting'
      if (this.checkinDisplay === 'hide') {
        return null
      }
      if (this.checkinDisplay !== 'show' && status === 'waiting' && data.customer !== undefined && !parseInt(data.customer)) {
        return null
      }
      return status
    },
    overviewTitle (event) {
      const data = event.data || {}
      if (data.kind !== 'resoverview') {
        return data.text || ''
      }
      return data.count + ' / ' + data.participants
    },
    checkinText (status) {
      if (status === 'in') {
        return this._('Checked in')
      }
      if (status === 'out') {
        return this._('Checked out')
      }
      return this._('Waiting for check-in')
    },
    summaryLabel (event, placement) {
      const data = event.data
      if (data.state === 'closed' || data.state === 'none' || placement.width < 30) {
        return ''
      }
      if (placement.width < 56) {
        return data.ratio + '%'
      }
      return formatDuration(Number(data.freeminutes) || 0, this._('h'), this._('min'))
    },
    stripGradient (event) {
      return bucketGradient(event.data.buckets)
    },
    occupancyLabel (event, placement) {
      const data = event.data
      const label = { count: data.count, participants: 0, duration: '' }
      if (placement.width >= 60 && data.participants) {
        label.participants = data.participants
      }
      if (placement.width >= (label.participants ? 110 : 70)) {
        label.duration = formatDuration(data.minutes, this._('h'), this._('min'))
      }
      return label
    },
    bandHover (e, event) {
      if (e.pointerType !== 'mouse' || !this.$refs.timeline || !this.$refs.wrapper) {
        return
      }
      const time = this.$refs.timeline.timeAt(e.clientX)
      if (this.embedded) {
        const pos = this.tooltipViewportPosition(e.clientX, e.clientY)
        this.hover = { event, time, x: pos.x, y: pos.y, clientX: e.clientX, clientY: e.clientY }
        this.$nextTick(() => this.measureTooltip())
        return
      }
      const rect = this.$refs.wrapper.getBoundingClientRect()
      this.hover = {
        event,
        time,
        x: e.clientX - rect.left + 14,
        y: e.clientY - rect.top + 14
      }
    },
    tooltipViewportPosition (clientX, clientY) {
      const margin = 8
      const gap = 14
      const width = this.tooltipSize.w
      const height = this.tooltipSize.h
      let x = clientX + gap
      let y = clientY + gap
      if (x + width > window.innerWidth - margin) {
        x = Math.max(margin, clientX - width - gap)
      }
      if (y + height > window.innerHeight - margin) {
        y = Math.max(margin, clientY - height - gap)
      }
      return { x, y }
    },
    measureTooltip () {
      const el = this.$refs.tooltip
      const hover = this.hover
      if (!el || !hover || !this.embedded || hover.clientX === undefined) {
        return
      }
      const w = el.offsetWidth
      const h = el.offsetHeight
      if (!w || !h || (w === this.tooltipSize.w && h === this.tooltipSize.h)) {
        return
      }
      this.tooltipSize = { w, h }
      const pos = this.tooltipViewportPosition(hover.clientX, hover.clientY)
      if (pos.x !== hover.x || pos.y !== hover.y) {
        this.hover = Object.assign({}, hover, pos)
      }
    },
    cachedTimes (data, time) {
      const key = data.shopid + '|' + data.source_id + '|' + dateKey(time)
      const entry = this.timesCache.get(key)
      if (!entry) {
        this.loadTimes(key, data, time)
        return null
      }
      return entry.value
    },
    async loadTimes (key, data, time) {
      const entry = { value: null }
      this.timesCache.set(key, entry)
      try {
        const response = await axios.get(this.baseDomain + data.shopid + '/' + (this.locale || 'fi_FI') + '/products/' + data.source_id + '/availabletimes/' + dateKey(time) + '.json?apikey=' + this.apiKey)
        entry.value = response.data || {}
      } catch (e) {
        entry.value = {}
      }
      this.timesVersion++
    },
    snapRange (rowKey, start, end) {
      if (this.mode !== 'hourly' || !this.isProductKey(rowKey)) {
        return null
      }
      const bands = this.bandsOfRow(rowKey)
      const s = nearestStart(bands, start.getTime())
      if (s === null) {
        return null
      }
      const band = bands.find(ev => isBookable(ev) && ev.start.getTime() <= s && ev.end.getTime() > s)
      const ends = band ? endTimesFor(this.cachedTimes(band.data, s), s) : []
      let e = nearestEnd(ends, Math.max(end.getTime(), s + 1))
      if (e === null) {
        e = Math.max(end.getTime(), s + 15 * 60000)
      }
      return { start: new Date(s), end: new Date(e) }
    },
    bandClicked (event, native) {
      if (!isBookable(event)) {
        return
      }
      let time = event.start.getTime()
      if (native && typeof native.clientX === 'number' && this.$refs.timeline) {
        time = this.$refs.timeline.timeAt(native.clientX)
      }
      const start = startForTap(event, time, s => endTimesFor(this.cachedTimes(event.data, s), s))
      if (start === null) {
        return
      }
      this.hover = null
      this.hasCapacityClicked(Object.assign({}, event.data, {
        start_date: new Date(start),
        end_date: new Date(start + 3600000),
        section_id: event.rowKeys[0],
        exact_end: false
      }))
    },

    // Day lens

    openLens (rootKey, date) {
      const root = this.findRow(rootKey)
      if (!root) {
        return
      }
      let source = null
      if (this.products && this.isProductKey(rootKey)) {
        source = { products: [rootKey.split('-')[1]] }
      } else if (this.isResourceSchedule && this.isResourceKey(rootKey)) {
        source = { resources: [rootKey.split('-')[0]] }
      }
      if (!source) {
        return
      }
      const day = startOfUnit(date, 'day').getTime()
      const [from, to] = this.lensWindow(rootKey, day)
      this.lens = {
        key: rootKey,
        source,
        label: root.label,
        day,
        from,
        to,
        sheet: typeof window !== 'undefined' && window.innerWidth < 600
      }
    },
    findRow (key) {
      const walk = nodes => {
        for (const node of nodes || []) {
          if (node.key === key) {
            return node
          }
          const found = walk(node.children)
          if (found) {
            return found
          }
        }
        return null
      }
      return walk(this.rows)
    },
    lensWindow (rootKey, day) {
      const dayEnd = addUnits(new Date(day), 'day', 1).getTime()
      let from = null
      let to = null
      for (const ev of this.timelineEvents) {
        const s = ev.start.getTime()
        const e = ev.end.getTime()
        if (e <= day || s >= dayEnd) {
          continue
        }
        if (isDaySummary(ev)) {
          if (ev.rowKeys[0] === rootKey && ev.data.windowfrom !== undefined) {
            from = from === null ? Number(ev.data.windowfrom) : Math.min(from, Number(ev.data.windowfrom))
            to = to === null ? Number(ev.data.windowto) : Math.max(to, Number(ev.data.windowto))
          }
        } else if (s >= day && e <= dayEnd && !ev.band && ev.rowKeys.some(key => key === rootKey || (!this.isProductKey(key) && this.rootKeys.get(key) === rootKey))) {
          // Reservations outside the start window stay visible in the lens
          from = from === null ? minuteOfDay(s) : Math.min(from, minuteOfDay(s))
          const endMinute = e === dayEnd ? 1440 : minuteOfDay(e)
          to = to === null ? endMinute : Math.max(to, endMinute)
        }
      }
      if (from === null || to === null) {
        return [0, 1440]
      }
      from = Math.floor(from / 60) * 60
      to = Math.min(1440, Math.ceil(to / 60) * 60)
      if (to - from < 240) {
        to = Math.min(1440, from + 240)
        from = Math.max(0, to - 240)
      }
      return [from, to]
    },
    navigateLens (delta) {
      if (!this.lens) {
        return
      }
      // Changing the day replaces the lens content. Keep the timeline and the page where they are.
      const restoreScroll = this.captureViewScroll()
      this.openLens(this.lens.key, addUnits(new Date(this.lens.day), 'day', delta))
      this.$nextTick(() => {
        restoreScroll()
        requestAnimationFrame(restoreScroll)
      })
    },
    captureViewScroll () {
      const nodes = []
      const wrapper = this.$refs.wrapper
      const scroller = wrapper && wrapper.querySelector('.st-scroller')
      if (scroller) {
        nodes.push(scroller)
      }
      let node = wrapper
      while (node) {
        nodes.push(node)
        node = node.parentElement
      }
      const saved = nodes.map(el => ({ el, top: el.scrollTop, left: el.scrollLeft }))
      return () => {
        for (const item of saved) {
          if (item.el.scrollTop !== item.top) {
            item.el.scrollTop = item.top
          }
          if (item.el.scrollLeft !== item.left) {
            item.el.scrollLeft = item.left
          }
        }
      }
    },
    closeLens () {
      this.lens = null
    },
    lensHeightChanged (height) {
      this.lensHeight = height
    },
    contentHeightChanged (height) {
      if (this.embedded) {
        this.contentHeight = height
        this.$emit('content-height', height)
      }
    },
    toLegacyEvent (event, overrides) {
      return Object.assign({}, event.data, {
        id: event.id,
        start_date: event.start,
        end_date: event.end,
        section_id: event.rowKeys.join(',')
      }, overrides || {})
    },

    // Loading

    createLoader () {
      if (this.loader) {
        this.loader.dispose()
      }
      this.rows = []
      this.timelineEvents = []
      this.loader = createRangeLoader({
        unit: this.unit,
        fetchChunk: async (from, to, signal) => {
          const url = this.scheduleLoadUrl
          const response = await axios.get(url + (url.indexOf('?') === -1 ? '?' : '&') + 'from=' + formatLoadDate(from) + '&to=' + formatLoadDate(to), { signal })
          return response.data
        },
        decorate: this.decorateEvent,
        onUpdate: (state) => {
          if (state.rows !== this.sourceRows) {
            this.sourceRows = state.rows
            this.rows = markRaw(this.embedded && this.products ? this.lensRowsFrom(state.rows) : state.rows)
            this.initOpenSections()
          }
          this.timelineEvents = markRaw(state.events)
          this.loading = state.loading
        }
      })
      const range = this.embedded ? this.lastRange : this.loadRange
      if (range) {
        this.loader.ensure(range.start, range.end)
      }
    },
    rangeChanged (range) {
      // Only the day lens loads its rendered range, which is fitted to one day
      if (!this.embedded) {
        return
      }
      this.lastRange = range
      if (this.loader) {
        this.loader.ensure(range.start, range.end)
      }
    },
    load () {
      this.timesCache.clear()
      if (this.loader) {
        this.loader.reload()
      }
    },
    lensRowsFrom (rows) {
      const root = rows && rows[0]
      if (!root) {
        return rows || []
      }
      const lift = row => Object.assign({}, row, { level: Math.max(0, row.level - 1), children: row.children ? row.children.map(lift) : undefined })
      return [Object.assign({}, root, { label: this._('Availability'), children: undefined }), ...(root.children || []).map(lift)]
    },
    reload () {
      this.debouncedReload()
    },
    initOpenSections () {
      if (!this.rows.length) {
        return
      }
      const keys = new Set(this.openSections)
      if (this.embedded) {
        const walk = rows => rows.forEach(row => {
          if (row.children && row.children.length) {
            keys.add(row.key)
            walk(row.children)
          }
        })
        walk(this.rows)
        this.openSectionsInitialized = true
      }
      if (!this.openSectionsInitialized) {
        for (const key of collectInitiallyOpen(this.rows)) {
          keys.add(key)
        }
        this.openSectionsInitialized = true
      }
      if (keys.size !== this.openSections.length) {
        this.openSections = [...keys]
      }
    },
    toggleSection (key, open) {
      if (open) {
        if (this.openSections.indexOf(key) === -1) {
          this.openSections = [...this.openSections, key]
        }
      } else {
        this.openSections = this.openSections.filter(k => k !== key)
      }
      this.saveViewState()
    },

    // Navigation and zoom

    anchorChanged (date) {
      this.anchor = date
      this.saveViewState()
    },
    labelWidthChanged (width) {
      this.labelWidth = width
      this.saveViewState()
    },
    moveBack () {
      this.anchor = addUnits(this.anchor, this.unit, -this.zoomParams[this.currentZoom].x_length)
      this.saveViewState()
    },
    moveForward () {
      this.anchor = addUnits(this.anchor, this.unit, this.zoomParams[this.currentZoom].x_length)
      this.saveViewState()
    },
    moveToToday () {
      const now = new Date()
      this.anchor = this.mode === 'hourly' ? startOfUnit(now, 'hour') : startOfUnit(now, 'day')
      this.saveViewState()
    },
    zoomIn () {
      if (this.currentZoom > 0) {
        this.currentZoom--
        this.saveViewState()
      }
    },
    zoomOut () {
      if (this.currentZoom < this.zoomParams.length - 1) {
        this.currentZoom++
        this.saveViewState()
      }
    },
    zoomGesture ({ direction }) {
      if (direction === 'in') {
        this.zoomIn()
      } else {
        this.zoomOut()
      }
    },
    toggleDensity () {
      this.density = this.nextDensity
      this.saveViewState()
    },

    // View state

    saveViewState () {
      this.saveViewStateToKey(this.storageKey)
    },
    saveViewStateToKey (key) {
      if (!key || !this.anchor || this.embedded) {
        return
      }
      const data = {
        view: 'timeline',
        zoom: this.currentZoom,
        zoomSize: this.zoomParams[this.currentZoom].x_size,
        date: startOfUnit(this.anchor, 'day').getTime(),
        time: this.anchor.getHours(),
        openSections: this.openSections,
        labelWidth: this.labelWidth,
        density: this.density
      }
      try {
        localStorage.setItem(key, JSON.stringify(data))
      } catch (e) {}
    },
    restoreViewState () {
      this.openSections = []
      this.openSectionsInitialized = false
      this.labelWidth = 400
      this.density = 'relaxed'
      this.currentZoom = 5
      if (typeof window !== 'undefined' && window.innerWidth < 600) {
        this.currentZoom = this.mode === 'hourly' ? 2 : 3
      } else if (typeof window !== 'undefined' && window.innerWidth < 1000) {
        this.currentZoom = this.mode === 'hourly' ? 3 : 4
      }
      let anchor = startOfUnit(new Date(), 'day')
      let hour = 8
      try {
        const data = localStorage.getItem(this.storageKey)
        if (data) {
          const state = JSON.parse(data)
          // A saved tree state wins over the folders the schedule marks open by default
          if (Array.isArray(state.openSections)) {
            this.openSections = state.openSections.slice()
            this.openSectionsInitialized = true
          }
          const sizeIndex = this.zoomParams.findIndex(z => z.x_size === Number(state.zoomSize))
          if (sizeIndex !== -1) {
            this.currentZoom = sizeIndex
          } else if (state.zoom !== false && state.zoom !== undefined && state.zoom !== null) {
            // Older states saved only the index, from before the two closest levels were added
            this.currentZoom = Math.max(0, Math.min((Number(state.zoom) || 0) + LEGACY_ZOOM_OFFSET, this.zoomParams.length - 1))
          }
          if (state.date) {
            anchor = startOfUnit(new Date(state.date), 'day')
          }
          if (state.time) {
            hour = Number(state.time)
          }
          if (state.labelWidth) {
            this.labelWidth = Number(state.labelWidth)
          }
          if (DENSITIES.includes(state.density)) {
            this.density = state.density
          } else if (state.density === 'comfortable') {
            this.density = 'relaxed'
          }
        }
      } catch (e) {
        // Ignore invalid stored state
      }
      if (this.currentZoom >= this.zoomParams.length) {
        this.currentZoom = this.zoomParams.length - 1
      }
      if (this.mode === 'hourly') {
        anchor.setHours(hour)
      }
      this.anchor = anchor
    },

    // Clicks

    eventBarClicked (event, clickedRowKey, native) {
      const rowKey = event.rowKeys[0]
      const data = event.data || {}
      if (data.kind === 'occupancy' || data.kind === 'overviewpeak') {
        this.openLens(this.isResourceSchedule ? rowKey : this.rootKeys.get(rowKey), event.start)
        return
      }
      if (this.isPoolKey(rowKey)) {
        return
      }
      if (this.isOverviewKey(rowKey)) {
        this.toggleSection(rowKey, this.openSections.indexOf(rowKey) === -1)
        return
      }
      if (this.isProductKey(rowKey)) {
        if (data.kind === 'daysummary') {
          this.openLens(rowKey, event.start)
        } else if (data.kind === 'band') {
          this.bandClicked(event, native)
        } else if (parseInt(data.capacity)) {
          this.hasCapacityClicked(this.toLegacyEvent(event))
        }
        return
      }
      this.eventClicked(this.toLegacyEvent(event))
    },
    hasCapacityClicked (event, resourceId) {
      if (this.products) {
        this.productHasCapacityClicked(event, resourceId)
      } else {
        this.resourceHasCapacityClicked(event, resourceId)
      }
    },
    async productHasCapacityClicked (event, resourceId) {
      let response
      try {
        response = await axios.get(this.baseDomain + event.shopid + '/products/' + event.source_id + '.json')
      } catch (e) {
        return
      }
      this.productShopId = event.shopid
      this.product = response.data
      this.summaryStart = DateTime.fromJSDate(event.start_date).toFormat('yyyy-MM-dd HH:mm')
      this.summaryEnd = DateTime.fromJSDate(event.end_date).toFormat('yyyy-MM-dd HH:mm')
      this.productStartDate = event.start_date
      // A tapped start leaves the length to the booking dialog's default
      this.productEndDate = event.exact_end === false ? null : event.end_date
      this.preselectedResource = resourceId || null
    },
    async resourceHasCapacityClicked (event, resourceId) {
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/resources/' + resourceId + '.json?apikey=' + this.apiKey)
      } catch (e) {
        return
      }
      const resource = response.data
      this.showModal('directreservation', DirectReservation, {
        props: {
          event: event,
          selectedResource: resource,
          shopId: this.shopId
        },
        events: {
          save: this.createReservation
        },
        handlers: {},
        width: 700
      })
    },
    rangeCreated ({ rowKey, start, end }) {
      if (this.isResourceSchedule) {
        const resourceId = rowKey.split('-')[0]
        this.hasCapacityClicked({ start_date: start, end_date: end, section_id: rowKey }, resourceId)
        return
      }
      if (this.isProductKey(rowKey)) {
        // A range selected on the availability band
        const bands = this.bandsOfRow(rowKey).filter(isBookable)
        const band = bands.find(ev => ev.start.getTime() <= start.getTime() && ev.end.getTime() > start.getTime()) || bands[0]
        if (band) {
          this.hasCapacityClicked(Object.assign({}, band.data, { start_date: start, end_date: end, section_id: rowKey }))
        }
        return
      }
      const root = this.rootRowOf(rowKey)
      if (!root) {
        return
      }
      const availability = this.timelineEvents.find(ev => ev.rowKeys.indexOf(root.key) !== -1 && ev.start.getTime() < end.getTime() && ev.end.getTime() > start.getTime())
      if (!availability) {
        return
      }
      let resourceId = null
      if (/^resource-/.test(rowKey)) {
        resourceId = rowKey.split('-')[1]
      }
      this.hasCapacityClicked({
        start_date: start,
        end_date: end,
        section_id: rowKey,
        source_id: availability.data.source_id,
        shopid: availability.data.shopid
      }, resourceId)
    },
    async createReservation (data) {
      let response
      const postData = {
        status: 1,
        source: 'editor',
        resources: data.resource.id + ':::' + data.resource.name + ':::' + data.participants + ';;;',
        start_date: DateTime.fromJSDate(data.start).toFormat('d.M.yyyy'),
        start_time: DateTime.fromJSDate(data.start).toFormat('H.mm'),
        end_date: DateTime.fromJSDate(data.end).toFormat('d.M.yyyy'),
        end_time: DateTime.fromJSDate(data.end).toFormat('H.mm'),
        maintenanceMode: data.maintenanceMode
      }
      try {
        response = await axios.post(this.baseDomain + this.shopId + '/reservations.json?apikey=' + this.apiKey, postData)
      } catch (e) {
        this.showError(e, this._('Creating the reservation failed!'))
        return
      }
      this.showNotification(this._('Reservation created!'))
      this.editReservation(response.data)
    },
    productModalClosed () {
      this.productShopId = null
      this.product = null
      this.productStartDate = null
      this.productEndDate = null
      this.preselectedResource = null
    },
    reservationAddedToBasket () {
      this.EventBus.$emit('showFloatingReceipt')
    },

    // Dragging existing reservations

    eventChanged (drag) {
      const event = drag.event
      const original = this.toLegacyEvent(event)
      let start = drag.start
      let end = drag.end
      let mode = drag.mode === 'move' ? 'move' : 'resize'
      const origKeys = event.rowKeys
      let newKeys = origKeys
      if (drag.toRowKey !== drag.fromRowKey) {
        newKeys = [...new Set(origKeys.map(key => (key === drag.fromRowKey ? drag.toRowKey : key)))]
      }
      const newSectionId = newKeys.join(',')
      if (this.keepTimes && newSectionId !== original.section_id) {
        const threshold = this.mode === 'daily' ? 43200000 : 1200000
        if (Math.abs(start.getTime() - event.start.getTime()) < threshold && Math.abs(end.getTime() - event.end.getTime()) < threshold) {
          start = event.start
          end = event.end
          mode = 'move'
        }
      }
      if (start.getTime() === event.start.getTime() && end.getTime() === event.end.getTime() && newSectionId === original.section_id) {
        return
      }
      const data = {
        id: event.id,
        source: 'editor'
      }
      if (this.mode === 'hourly') {
        data.start_date = DateTime.fromJSDate(start).toFormat('d.M.yyyy')
        data.start_time = DateTime.fromJSDate(start).toFormat('H.mm')
        data.end_date = DateTime.fromJSDate(end).toFormat('d.M.yyyy')
        data.end_time = DateTime.fromJSDate(end).toFormat('H.mm')
      } else {
        const s = DateTime.fromJSDate(event.start).set({ hour: 0, minute: 0, second: 0 })
        const e = DateTime.fromJSDate(event.end).set({ hour: 0, minute: 0, second: 0 })
        const diff = e.diff(s, 'days')
        data.start_date = DateTime.fromJSDate(start).toFormat('d.M.yyyy')
        const realStart = original.real_start_date
        const realEnd = original.real_end_date
        data.start_time = realStart
          ? DateTime.fromFormat(realStart, 'yyyy-MM-dd HH:mm').toFormat('H.mm')
          : DateTime.fromJSDate(event.start).toFormat('H.mm')
        if (mode === 'move') {
          data.end_date = DateTime.fromFormat(data.start_date, 'd.M.yyyy').plus({ days: Math.round(diff.days) }).toFormat('d.M.yyyy')
        } else {
          data.end_date = DateTime.fromJSDate(end).toFormat('d.M.yyyy')
        }
        data.end_time = realEnd
          ? DateTime.fromFormat(realEnd, 'yyyy-MM-dd HH:mm').toFormat('H.mm')
          : DateTime.fromJSDate(event.end).toFormat('H.mm')
      }
      if (newSectionId !== original.section_id) {
        data.resources = this.changeResources(original.section_id, newSectionId)
      } else {
        const ids = newKeys.map(id => {
          const parts = id.split('-')
          return parts.length === 2 ? parts[0] : parts[1]
        })
        data.resources = [...new Set(ids)].join(',')
      }
      const restore = this.applyLocalChange(event, start, end, newKeys)
      const save = async (joinIds) => {
        try {
          await axios.patch(this.baseDomain + this.shopId + '/reservations/' + data.id + '.json?apikey=' + this.apiKey, joinIds ? Object.assign({}, data, { joinpartials: joinIds }) : data)
        } catch (e) {
          this.showError(e)
          restore()
          return
        }
        this.reload()
      }
      const joinIds = this.findJoinablePartials(event, start, end, newKeys)
      if (joinIds.length) {
        this.showDialog({
          title: this._('This part touches another part of the same reservation. Do you want to join them?'),
          buttons: [
            {
              title: this._('Join parts'),
              class: 'success',
              icon: 'link',
              handler: () => save(joinIds)
            },
            {
              title: this._('Move only'),
              icon: 'save',
              handler: () => save()
            },
            {
              title: this._('Cancel'),
              icon: 'undo',
              handler: restore
            }
          ]
        })
        return
      }
      this.showDialog({
        title: this._('Do you want to save the change you made to this reservation?'),
        buttons: [
          {
            title: this._('Save'),
            class: 'success',
            icon: 'save',
            handler: () => save()
          },
          {
            title: this._('Cancel'),
            icon: 'undo',
            handler: restore
          }
        ]
      })
    },
    // Other parts of the same split booking on the same resources that the new range overlaps or touches,
    // chained in both directions. Hourly mode allows one snap step between them, daily mode the same day.
    findJoinablePartials (event, start, end, newKeys) {
      const itemId = event.data && event.data.partial_item_id
      if (!itemId) {
        return []
      }
      const resourceSet = keys => [...new Set(this.stripIndexes(keys))].sort().join(',')
      const resources = resourceSet(newKeys)
      const pending = this.timelineEvents.filter(ev => ev.id !== event.id && ev.data && String(ev.data.partial_item_id) === String(itemId) && resourceSet(ev.rowKeys) === resources)
      const hourly = this.mode === 'hourly'
      const tolerance = hourly ? 15 * 60000 : 0
      const time = date => (hourly ? date : startOfUnit(date, 'day')).getTime()
      let from = time(start)
      let to = time(end)
      const ids = new Set()
      let grown = true
      while (grown) {
        grown = false
        for (const ev of pending) {
          if (!ids.has(ev.id) && time(ev.start) <= to + tolerance && time(ev.end) >= from - tolerance) {
            from = Math.min(from, time(ev.start))
            to = Math.max(to, time(ev.end))
            ids.add(ev.id)
            grown = true
          }
        }
      }
      return [...ids]
    },
    applyLocalChange (event, start, end, rowKeys) {
      const updated = Object.assign({}, event, { start, end, rowKeys })
      this.timelineEvents = markRaw(this.timelineEvents.map(ev => (ev === event ? updated : ev)))
      return () => {
        this.timelineEvents = markRaw(this.timelineEvents.map(ev => (ev === updated ? event : ev)))
      }
    },
    changeResources (origStr, newStr) {
      const origSections = origStr.split(',')
      const newSections = newStr.split(',')
      const removedSections = origSections.filter(item => !newSections.includes(item))
      const addedSections = newSections.filter(item => !origSections.includes(item))
      const origIds = this.stripIndexes(origSections)
      const removedIds = this.stripIndexes(removedSections)
      const addedIds = this.stripIndexes(addedSections)
      const finalIds = []
      for (const id of origIds) {
        if (removedIds.indexOf(id) === -1) {
          finalIds.push(id)
        }
      }
      for (const id of addedIds) {
        finalIds.push(id)
      }
      return finalIds.join(',')
    },
    stripIndexes (sections) {
      // Resource schedule keys have two parts (367-0), product schedule keys three (resource-367-0)
      return sections.map(section => {
        const parts = section.split('-')
        return parts.length === 2 ? parts[0] : parts[1]
      })
    },

    // Reservation preview

    async eventClicked (event) {
      let response
      let reservation = null
      let order = null
      if (parseInt(event.id)) {
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/reservations/' + event.id + '.json?items=true&apikey=' + this.apiKey)
          reservation = response.data
        } catch (e) {}
      }
      if (parseInt(event.order_id)) {
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/orders/' + event.order_id + '.json?items=true&reservations=true&apikey=' + this.apiKey)
          order = response.data
        } catch (e) {}
      }
      this.showModal('reservationpreview', ReservationPreview, {
        props: {
          reservation: reservation,
          order: order,
          customTitle: event.text,
          channel: CHANNEL_NAMES[event.channel] ? event.channel : null,
          channelName: CHANNEL_NAMES[event.channel] || '',
          pickup: event.pickup || null,
          dropoff: event.dropoff || null
        },
        events: {
          displayOrder: this.displayOrder,
          editReservation: this.editReservation,
          checkin: this.checkin(event),
          checkout: this.checkout(event),
          cancelCheckin: this.cancelCheckin(event),
          cancelCheckout: this.cancelCheckout(event),
          saveNotes: this.saveNotes(event),
          selectPreviousPartialReservation: this.selectPreviousPartialReservation(event),
          selectNextPartialReservation: this.selectNextPartialReservation(event)
        },
        handlers: {},
        width: 800
      })
    },
    checkin (event) {
      return (item) => {
        this.checkInOrOut(event, item, 'in')
      }
    },
    checkout (event) {
      return (item) => {
        this.checkInOrOut(event, item, 'out')
      }
    },
    cancelCheckin (event) {
      return (item) => {
        this.checkInOrOut(event, item, 'in', true)
      }
    },
    cancelCheckout (event) {
      return (item) => {
        this.checkInOrOut(event, item, 'out', true)
      }
    },
    async checkInOrOut (event, item, mode, cancel) {
      const data = {
        identifier: item.identifier
      }
      if (mode === 'in') {
        data.checkin_ts = cancel ? null : 'now'
      } else {
        data.checkout_ts = cancel ? null : 'now'
      }
      try {
        await axios.patch(this.baseDomain + this.shopId + '/reservations/' + item.identifier + '.json?apikey=' + this.apiKey, data)
      } catch (e) {}
      this.eventClicked(event)
    },
    saveNotes () {
      return async (order) => {
        const data = {
          id: order.id,
          notes: order.notes
        }
        try {
          await axios.patch(this.baseDomain + this.shopId + '/orders/' + order.id + '.json?apikey=' + this.apiKey, data)
        } catch (e) {
          this.showError(e)
        }
      }
    },
    findPartialItem (order, reservation) {
      let index = null
      let count = 0
      let item = null
      for (const currItem of order.ownItem) {
        const currIndex = currItem.ownReservation.findIndex(res => res && Number(res.id) === Number(reservation.id))
        if (currIndex !== -1) {
          item = currItem
          index = currIndex
          count = currItem.ownReservation.filter(res => res && Number(res.item_id) === Number(reservation.item_id)).length
          break
        }
      }
      return { item, index, count }
    },
    selectPreviousPartialReservation (event) {
      return ({ order, reservation, text }) => {
        let { item, index, count } = this.findPartialItem(order, reservation)
        if (!item) {
          return
        }
        index--
        if (index < 0) {
          index = count - 1
        }
        this.eventClicked({
          id: item.ownReservation[index].id,
          order_id: order.id,
          text: text,
          channel: event.channel
        })
      }
    },
    selectNextPartialReservation (event) {
      return ({ order, reservation, text }) => {
        let { item, index, count } = this.findPartialItem(order, reservation)
        if (!item) {
          return
        }
        index++
        if (index >= count) {
          index = 0
        }
        this.eventClicked({
          id: item.ownReservation[index].id,
          order_id: order.id,
          text: text,
          channel: event.channel
        })
      }
    },
    displayOrder (id, skipState) {
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'OrderDetails',
        title: this._('Order') + ': ' + id,
        width: 6,
        data: {
          orderId: id
        },
        skipState
      })
    },
    editReservation (id, orderId, name, skipState) {
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'ReservationDetails',
        title: this._('Reservation') + ': ' + id + (name ? ': ' + name : ''),
        data: {
          reservationId: id,
          orderId: orderId
        },
        skipState
      })
    },
    async showPreviewModal (shopId, sourceId) {
      let response
      try {
        response = await axios.get(this.baseDomain + shopId + '/products/' + sourceId + '.json')
      } catch (e) {
        return
      }
      this.showModal('productpopup', ProductPopup, {
        props: {
          product: response.data,
          shopId: this.shopId,
          productShopId: shopId,
          baseDomain: this.baseDomain,
          locale: this.locale
        },
        events: {},
        handlers: {},
        width: 1000,
        maxWidth: 1000,
        maxHeight: 500,
        scrollable: true
      })
    },

    // Sizing

    updateContainerHeight () {
      const BOTTOM_BUFFER = 10
      const MIN_HEIGHT = 200
      const wrapper = this.$refs.wrapper
      if (!wrapper) {
        return
      }
      if (wrapper.closest('.modal-content')) {
        this.containerHeight = this.maxHeight || '60vh'
        return
      }
      const posColumn = wrapper.closest('.pos-column')
      if (!posColumn) {
        return
      }
      const scrollAncestor = posColumn.querySelector(':scope > div:first-of-type')
      const timeline = this.$refs.timeline && this.$refs.timeline.$el
      if (!scrollAncestor || !timeline) {
        return
      }
      const ancestorRect = scrollAncestor.getBoundingClientRect()
      const timelineRect = timeline.getBoundingClientRect()
      // clientHeight includes padding. Below 992px the stacked columns (johkupos-main-app.scss, total-columns-N) pad
      // the bottom of this element for the following columns' tabs, which cover it, so the timeline must end above
      // the padding (JDEV-3281). Desktop has no padding here, so its size is unchanged.
      const paddingBottom = parseFloat(getComputedStyle(scrollAncestor).paddingBottom) || 0
      const height = scrollAncestor.clientHeight - (timelineRect.top - ancestorRect.top) - Math.max(paddingBottom, BOTTOM_BUFFER)
      if (height >= MIN_HEIGHT) {
        this.containerHeight = height
      }
    },
    updateToolbarWidth () {
      const wrapper = this.$refs.wrapper
      if (!wrapper || !wrapper.clientWidth) {
        return
      }
      const narrow = wrapper.clientWidth < TOOLBAR_WIDE
      if (narrow !== this.toolbarNarrow) {
        this.toolbarNarrow = narrow
        this.debouncedResize()
      }
    },
    initResizeObserver () {
      if (this.$refs.wrapper && typeof ResizeObserver !== 'undefined') {
        this.widthObserver = new ResizeObserver(() => this.updateToolbarWidth())
        this.widthObserver.observe(this.$refs.wrapper)
      }
      const posColumn = this.$refs.wrapper && this.$refs.wrapper.closest('.pos-column')
      if (!posColumn || typeof ResizeObserver === 'undefined') {
        return
      }
      this.observer = new ResizeObserver(() => this.debouncedResize())
      this.observer.observe(posColumn)
      const nav = posColumn.querySelector('.header-navbar')
      if (nav) {
        this.observer.observe(nav)
      }
    },
    setupEventBus () {
      if (this.events || !this.reloadEvents) {
        this.enableEventBus()
        return
      }
      const handlers = {}
      for (const eventType of this.reloadEvents) {
        handlers[eventType] = (message) => {
          if (!message || !message.data) {
            return
          }
          // Only reload on availabilityCacheUpdates that affect the products in this view
          if (eventType === 'availabilityCacheUpdate' && this.products && message.data.affectedGlobalProducts) {
            const intersection = this.products.filter(x => message.data.affectedGlobalProducts.includes(parseInt(x, 10)))
            if (intersection.length) {
              this.reload()
            }
          } else if (eventType === 'componentUpdate' && message.shopId === this.shopId) {
            if (message.data.id === this.component.id) {
              this.reload()
            }
          } else if (eventType === 'reservationUpdate' && message.shopId === this.shopId) {
            if (message.data.id && this.loader && this.loader.hasEvent(message.data.id)) {
              this.reload()
            }
          } else if (message.shopId === this.shopId) {
            this.reload()
          }
        }
      }
      this.events = handlers
      if (this.embedded) {
        this.enableEventBus()
        return
      }
      handlers.columnRedrawTriggered = () => {
        this.debouncedResize()
      }
      handlers.restoreDetails = (message) => {
        if (message.shopId !== this.shopId) {
          return
        }
        switch (message.component) {
          case 'ReservationDetails':
            this.editReservation(message.data.reservationId, message.data.orderId, null, true)
            break
          case 'OrderDetails':
            this.displayOrder(message.data.orderId, true)
            break
        }
      }
      this.events = handlers
      this.enableEventBus()
    }
  },
  created () {
    this.debouncedReload = debounce(() => this.load(), 500)
    this.debouncedResize = debounce(() => this.updateContainerHeight(), 100)
    this.timesCache = new Map()
    this.sourceRows = null
    if (this.embedded) {
      this.anchor = startOfUnit(this.embedded.start, 'hour')
      this.labelWidth = this.embeddedLabelWidth || 400
      this.density = this.embeddedDensity || 'relaxed'
      this.currentZoom = 0
    } else {
      this.restoreViewState()
    }
    this.createLoader()
  },
  mounted () {
    this.setupEventBus()
    if (this.embedded) {
      return
    }
    this.updateContainerHeight()
    this.initResizeObserver()
    window.addEventListener('resize', this.debouncedResize)
    this.EventBus.$on('redrawDataTable', this.debouncedResize)
  },
  unmounted () {
    this.saveViewState()
    if (this.loader) {
      this.loader.dispose()
    }
    window.removeEventListener('resize', this.debouncedResize)
    this.EventBus.$off('redrawDataTable', this.debouncedResize)
    this.debouncedResize.cancel()
    this.debouncedReload.cancel()
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
    if (this.widthObserver) {
      this.widthObserver.disconnect()
      this.widthObserver = null
    }
  }
}
</script>

<style>
.timeline-view-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  padding: 10px 0 8px;
}
.timeline-view-nav, .timeline-view-tools {
  display: flex;
  align-items: center;
  gap: 5px;
}
.timeline-view-nav {
  order: 1;
}
.timeline-view-toolbar .timeline-view-legend {
  order: 2;
}
.timeline-view-tools {
  order: 3;
}
.timeline-view-toolbar-narrow .timeline-view-tools {
  order: 2;
}
.timeline-view-toolbar-narrow .timeline-view-legend {
  order: 3;
  flex-basis: 100%;
}
.timeline-view-toolbar-narrow, .timeline-view-toolbar-narrow .timeline-view-nav, .timeline-view-toolbar-narrow .timeline-view-tools {
  gap: 4px;
}
.timeline-view-toolbar .btn-xs {
  height: 36px;
  min-width: 36px;
  padding: 0 10px;
  border-radius: 6px;
}
.timeline-view .timeline-view-toolbar .form-control {
  height: 36px;
  padding-top: 0;
  padding-bottom: 0;
}
.width-xxs .timeline-view-toolbar .btn-xs {
  min-width: 28px;
  padding: 0 4px;
}
.width-xxs .timeline-view .timeline-view-toolbar .vuedatepicker {
  width: 92px;
}
.width-xxs .timeline-view .timeline-view-toolbar .vuetimepicker {
  width: 60px;
}
.width-xxs .timeline-view-toolbar .js-datepicker-input, .width-xxs .timeline-view-toolbar .js-timepicker-input {
  padding-left: 8px;
}
.timeline-view-toolbar .daterange-date {
  margin-bottom: 0;
  margin-right: 0;
}
.timeline-view-density {
  flex: none;
  margin-left: -4px;
  border: 0;
  background: transparent;
  color: var(--st-muted);
  width: 28px;
  height: 28px;
  border-radius: 6px;
  cursor: pointer;
}
.timeline-view-density:hover {
  background: rgba(0, 0, 0, 0.05);
}
.timeline-view-info {
  flex: none;
  color: #3499db;
  cursor: pointer;
  padding: 4px;
  margin: -4px -2px;
}
.timeline-view-shared {
  margin-left: 6px;
  color: #8a96a3;
  font-size: 11px;
}
.timeline-view-center {
  width: 100%;
  text-align: center;
}
.timeline-view-capacity {
  font-size: 15px;
  font-weight: 700;
}
.timeline-view-res {
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  min-width: 0;
}
.timeline-view-res-count {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-weight: 700;
}
.timeline-view-res-count .fa, .timeline-view-res-detail .fa {
  font-size: 10px;
  opacity: 0.75;
}
.timeline-view-res-dot {
  flex: none;
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #43a047;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.8);
}
.timeline-view-res-two {
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  gap: 2px;
}
.timeline-view-res-line {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  width: 100%;
}
.timeline-view-res-detail {
  font-size: 11px;
  font-weight: 400;
  opacity: 0.85;
}
.timeline-view-res-time {
  flex: none;
}
.timeline-view-res-status {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}
.timeline-view-res-name {
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.timeline-view-res-notes {
  flex: none;
  font-size: 11px;
  opacity: 0.75;
}
.timeline-view-res-channel {
  flex: none;
  display: inline-flex;
  align-items: center;
  margin-left: auto;
}
.timeline-view-res-channel img {
  display: block;
  width: 14px;
  height: 14px;
  border-radius: 3px;
}
.timeline-view-res-channel .fa-globe {
  font-size: 12px;
  opacity: 0.75;
}
.timeline-view-res-channel-text {
  font-size: 9px;
  font-weight: bold;
  opacity: 0.75;
}

.timeline-view .st-event.sv-paid, .timeline-view .st-event.sv-default {
  background: #eef4c4;
  border-left-color: #a9b91f;
  color: #4d5410;
}
.timeline-view .st-event.sv-reservation {
  background: #e6ebee;
  border-left-color: #90a4ae;
  color: #37474f;
}
.timeline-view .st-event.sv-open {
  background: #d6e9f8;
  border-left-color: #237ebb;
  color: #134a72;
}
.timeline-view .st-event.sv-confirmed {
  background: #fff4b8;
  border-left-color: #e0c84a;
  color: #6b5c00;
}
.timeline-view .st-event.sv-unpaid {
  background: #fbdcdc;
  border-left-color: #e57373;
  color: #6d1b1b;
}
.timeline-view .st-event.sv-partial {
  background: #ffe7a3;
  border-left-color: #ffb300;
  color: #5c4100;
}
.timeline-view .st-event.sv-available {
  background: #76b841;
  border: 0;
  color: #fff;
}
.timeline-view .st-event.sv-unavailable {
  background: #f6d2d2;
  border: 0;
  color: #7f1d1d;
}
.timeline-view .st-event.sv-overview {
  --fill: 0%;
  background: linear-gradient(to top, rgba(169, 185, 31, 0.55) var(--fill), #f5f8e3 var(--fill));
  border-left-color: #a9b91f;
  color: #4d5410;
  font-weight: 700;
}
.timeline-view .st-event.sv-overview.reserved_10 { --fill: 10%; }
.timeline-view .st-event.sv-overview.reserved_20 { --fill: 20%; }
.timeline-view .st-event.sv-overview.reserved_30 { --fill: 30%; }
.timeline-view .st-event.sv-overview.reserved_40 { --fill: 40%; }
.timeline-view .st-event.sv-overview.reserved_50 { --fill: 50%; }
.timeline-view .st-event.sv-overview.reserved_60 { --fill: 60%; }
.timeline-view .st-event.sv-overview.reserved_70 { --fill: 70%; }
.timeline-view .st-event.sv-overview.reserved_80 { --fill: 80%; }
.timeline-view .st-event.sv-overview.reserved_90 { --fill: 90%; }
.timeline-view .st-event.sv-overview.reserved_100 { --fill: 100%; }
.timeline-view .st-event.sv-current {
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.12) inset;
}

.timeline-view {
  position: relative;
}
.timeline-view-legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 12px;
  font-size: 12px;
  color: #4b5563;
}
.timeline-view-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
}
.timeline-view-swatch {
  display: inline-block;
  width: 14px;
  height: 10px;
  border-radius: 2px;
}
.timeline-view-swatch-free { background: #76b841; }
.timeline-view-swatch-low, .timeline-view-swatch-partial { background: #f0b429; }
.timeline-view-swatch-none { background: #f3b4b4; }
.timeline-view-swatch-closed { background: repeating-linear-gradient(135deg, #f6d2d2 0 3px, #fbe9e9 3px 6px); }

.timeline-view .st-event.sv-band {
  border: 0;
  border-radius: 0;
  box-shadow: none;
}
.timeline-view .st-event.sv-band .st-event-body {
  padding: 0;
}
.timeline-view .st-event.sv-band-free {
  background: #76b841;
  color: #fff;
  cursor: crosshair;
}
.timeline-view .st-event.sv-band-low {
  background: #f0b429;
  color: #4a3200;
  cursor: crosshair;
}
.timeline-view .st-event.sv-band-none {
  background: #f3b4b4;
  color: #7f1d1d;
  cursor: default;
}
.timeline-view .st-event.sv-band-closed {
  background: repeating-linear-gradient(135deg, #f6d2d2 0 6px, #fbe9e9 6px 12px);
  cursor: default;
}
.timeline-view .st-event.sv-band-outside {
  background: repeating-linear-gradient(135deg, #eef1f4 0 6px, #f7f8fa 6px 12px);
  cursor: default;
}
.timeline-view .st-event.sv-band:hover {
  box-shadow: none;
}
.timeline-view .st-event.sv-band-free:hover, .timeline-view .st-event.sv-band-low:hover {
  filter: brightness(1.06);
}
.timeline-view .st-ghost.st-ghost-create {
  background: rgba(255, 255, 255, 0.55);
  border-color: #2f6b16;
  color: #1f3d10;
}
.timeline-view-band {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
}
.timeline-view-ticks {
  position: absolute;
  bottom: 0;
  height: 6px;
  background-image: linear-gradient(to right, rgba(255, 255, 255, 0.85) 1px, transparent 1px);
  background-repeat: repeat-x;
  pointer-events: none;
}
.timeline-view-tick {
  position: absolute;
  bottom: 0;
  width: 1px;
  height: 6px;
  background: rgba(255, 255, 255, 0.85);
  pointer-events: none;
}
.sv-band-low .timeline-view-ticks {
  background-image: linear-gradient(to right, rgba(74, 50, 0, 0.5) 1px, transparent 1px);
}
.sv-band-low .timeline-view-tick {
  background: rgba(74, 50, 0, 0.5);
}

.timeline-view .st-event.sv-daysummary {
  border: 0;
}
.timeline-view .st-event.sv-daysummary-free {
  background: #76b841;
  color: #fff;
}
.timeline-view .st-event.sv-daysummary-partial {
  background: #f0b429;
  color: #4a3200;
}
.timeline-view .st-event.sv-daysummary-none {
  background: #f3b4b4;
  color: #7f1d1d;
}
.timeline-view .st-event.sv-daysummary-closed {
  background: repeating-linear-gradient(135deg, #f6d2d2 0 6px, #fbe9e9 6px 12px);
  color: #7f1d1d;
}
.timeline-view-summary {
  font-size: 12px;
  font-weight: 700;
  padding-bottom: 4px;
}
.timeline-view-strip {
  position: absolute;
  left: 4px;
  right: 4px;
  bottom: 3px;
  height: 4px;
  border-radius: 2px;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.7);
  pointer-events: none;
}

.timeline-view .st-event.sv-occupancy {
  background: #e8f1fb;
  border-left-color: #3499db;
  color: #134a72;
  font-weight: 700;
}
.timeline-view-occupancy .fa-user {
  margin-right: 2px;
  opacity: 0.7;
}
.timeline-view-occupancy-sep {
  margin: 0 4px;
  font-weight: 400;
  opacity: 0.6;
}
.timeline-view-occupancy-duration {
  margin-left: 6px;
  font-weight: 400;
}
.timeline-view .st-event.sv-occupancy.sv-dimmed {
  opacity: 0.35;
}

.timeline-view-tooltip {
  position: absolute;
  z-index: 30;
  padding: 6px 9px;
  border-radius: 6px;
  background: rgba(17, 24, 39, 0.92);
  color: #fff;
  font-size: 12px;
  line-height: 1.45;
  white-space: nowrap;
  pointer-events: none;
}
.timeline-view-tooltip-fixed {
  position: fixed;
  z-index: 4000;
}
.timeline-view-tooltip-time {
  font-weight: 700;
}
</style>
